import { useState, useEffect } from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useApplicantContext } from '../contexts/ApplicantContext';
import { Card, Badge, Button, Input, Label, Select } from '../components/ui';
import { PageHeader } from '../components/PageHeader';
import { Lock } from 'lucide-react';
import { humanize } from '../lib/humanize';
import { ProfileTestScoresEditor } from '../components/profile/ProfileTestScoresEditor';
import { ProfileHonorsEditor } from '../components/profile/ProfileHonorsEditor';
import { ProfileActivitiesEditor } from '../components/profile/ProfileActivitiesEditor';
import { ProfileTargetSchoolsEditor } from '../components/profile/ProfileTargetSchoolsEditor';
import { ProfileDemographicsEditor } from '../components/profile/ProfileDemographicsEditor';
import { ProfileCommunityServiceEditor } from '../components/profile/ProfileCommunityServiceEditor';
import type { Profile } from '@scholarship-app/types';

export function ProfilePage() {
  const { user, membership } = useAuth();
  const { selectedApplicantId, selectedApplicant, profile, loading } = useApplicantContext();
  const applicantId = selectedApplicantId;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Profile | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Reset draft whenever the underlying profile changes (e.g. switched applicants).
    if (profile) {
      setDraft(profile);
      setEditing(false);
    } else {
      setDraft(null);
    }
  }, [profile, selectedApplicantId]);

  const isApplicant = membership?.role === 'applicant';

  async function handleSave() {
    if (!db || !membership || !applicantId || !user || !draft) return;
    setSaving(true);
    try {
      const ref = doc(db, 'households', membership.householdId, 'applicants', applicantId, 'profile', 'profile');
      await setDoc(ref, { ...draft, updated_at: serverTimestamp() }, { merge: true });
      setEditing(false);
    } catch (e) {
      alert('Save failed: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-soft">Loading profile…</div>;
  if (!profile || !draft) {
    return (
      <div className="space-y-4 max-w-4xl">
        <PageHeader
          title={`${selectedApplicant?.display_name ?? 'Applicant'} — Profile`}
          description="Master profile used for matching and autofill."
        />
        <Card className="p-6 text-center text-soft">
          No profile data yet for {selectedApplicant?.display_name ?? 'this applicant'}.
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-4xl">
      <PageHeader
        title={`${profile.preferred_name || profile.legal_first_name || selectedApplicant?.display_name || 'Applicant'} — Profile`}
        description="Master profile used for matching and autofill."
        action={
          editing ? (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => { setDraft(profile); setEditing(false); }}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Button>
            </div>
          ) : (
            <Button onClick={() => setEditing(true)}>Edit profile</Button>
          )
        }
      />

      <Card className="p-4 space-y-4">
        <h2 className="font-medium text-ink">Identity</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Legal First Name</Label>
            <Input value={draft.legal_first_name ?? ''} onChange={(e) => setDraft({ ...draft, legal_first_name: e.target.value })} disabled={!editing} />
          </div>
          <div>
            <Label>Middle</Label>
            <Input value={draft.legal_middle_name ?? ''} onChange={(e) => setDraft({ ...draft, legal_middle_name: e.target.value })} disabled={!editing} />
          </div>
          <div>
            <Label>Last Name</Label>
            <Input value={draft.legal_last_name ?? ''} onChange={(e) => setDraft({ ...draft, legal_last_name: e.target.value })} disabled={!editing} />
          </div>
          <div>
            <Label>Preferred Name</Label>
            <Input value={draft.preferred_name ?? ''} onChange={(e) => setDraft({ ...draft, preferred_name: e.target.value })} disabled={!editing} />
          </div>
          <div>
            <Label>
              Date of birth {isApplicant && <Lock size={12} className="inline ml-1 text-amber-600" />}
            </Label>
            <Input type="date" value={draft.date_of_birth ?? ''} onChange={(e) => setDraft({ ...draft, date_of_birth: e.target.value })} disabled={!editing || isApplicant} />
          </div>
          <div>
            <Label>Gender</Label>
            <Input value={draft.gender ?? ''} onChange={(e) => setDraft({ ...draft, gender: e.target.value })} disabled={!editing} />
          </div>
        </div>
        <div>
          <Label>Email</Label>
          <Input type="email" value={draft.email ?? ''} onChange={(e) => setDraft({ ...draft, email: e.target.value })} disabled={!editing} />
        </div>
      </Card>

      <Card className="p-4 space-y-4">
        <h2 className="font-medium text-ink">Education</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Schooling Type</Label>
            <Select value={draft.schooling_type} onChange={(e) => setDraft({ ...draft, schooling_type: e.target.value as Profile['schooling_type'] })} disabled={!editing}>
              <option value="homeschool_npns">Homeschool (NC NPNS)</option>
              <option value="public">Public</option>
              <option value="private">Private</option>
              <option value="charter">Charter</option>
              <option value="online">Online</option>
            </Select>
          </div>
          <div>
            <Label>Co-op</Label>
            <Input value={draft.homeschool_co_op ?? ''} onChange={(e) => setDraft({ ...draft, homeschool_co_op: e.target.value })} disabled={!editing} />
          </div>
          <div>
            <Label>Expected Graduation</Label>
            <Input value={draft.expected_graduation ?? ''} onChange={(e) => setDraft({ ...draft, expected_graduation: e.target.value })} disabled={!editing} placeholder="YYYY-MM" />
          </div>
          <div>
            <Label>Weighted GPA</Label>
            <Input type="number" step="0.01" value={draft.gpa_weighted ?? ''} onChange={(e) => setDraft({ ...draft, gpa_weighted: e.target.value ? Number(e.target.value) : null })} disabled={!editing} />
          </div>
          <div>
            <Label>Unweighted GPA</Label>
            <Input type="number" step="0.01" value={draft.gpa_unweighted ?? ''} onChange={(e) => setDraft({ ...draft, gpa_unweighted: e.target.value ? Number(e.target.value) : null })} disabled={!editing} />
          </div>
        </div>
      </Card>

      <Card className="p-4 space-y-4">
        <h2 className="font-medium text-ink">Test Scores</h2>
        <ProfileTestScoresEditor
          testScores={draft.test_scores}
          editing={editing}
          onChange={(next) => setDraft({ ...draft, test_scores: next })}
        />
      </Card>

      <Card className="p-4 space-y-4">
        <h2 className="font-medium text-ink">Target Schools</h2>
        <ProfileTargetSchoolsEditor editing={editing} />
      </Card>

      <Card className="p-4 space-y-4">
        <h2 className="font-medium text-ink">Honors & Awards</h2>
        <ProfileHonorsEditor
          honors={draft.honors_awards}
          editing={editing}
          onChange={(next) => setDraft({ ...draft, honors_awards: next })}
        />
      </Card>

      <Card className="p-4 space-y-4">
        <h2 className="font-medium text-ink">Activities</h2>
        <ProfileActivitiesEditor
          activities={draft.extracurriculars}
          editing={editing}
          onChange={(next) => setDraft({ ...draft, extracurriculars: next })}
        />
      </Card>

      <Card className="p-4 space-y-4">
        <h2 className="font-medium text-ink">Community Service</h2>
        <div className="flex items-center gap-2 mb-3">
          <Label htmlFor="cs_hours">Total community service hours</Label>
          <Input
            id="cs_hours"
            type="number"
            min="0"
            value={draft.community_service_hours_total ?? 0}
            onChange={(e) => setDraft({ ...draft, community_service_hours_total: Number(e.target.value) })}
            disabled={!editing}
            className="!w-24"
          />
        </div>
        <ProfileCommunityServiceEditor
          entries={draft.community_service_entries}
          editing={editing}
          onChange={(next) => setDraft({ ...draft, community_service_entries: next })}
        />
      </Card>

      <Card className="p-4 space-y-4">
        <h2 className="font-medium text-ink">Affiliations & Demographics</h2>
        <ProfileDemographicsEditor
          draft={draft}
          editing={editing}
          isApplicantRole={isApplicant}
          onChange={(next) => setDraft(next)}
        />
      </Card>

      <Card className="p-4">
        <h2 className="font-medium text-ink mb-3">Computed Tags (Used for Matching)</h2>
        <div className="flex flex-wrap gap-1">
          {draft.tags.map((t) => <Badge key={t} variant="indigo">{humanize(t)}</Badge>)}
        </div>
      </Card>
    </div>
  );
}
